/* Native delivery-estimate widget (issue #316, replaces the Omega "S: Estimated
   Delivery Date" app). Spec: live-theme/pdp-performance/EDD-NATIVE-SPEC.md.

   Loaded once, globally, as a deferred asset (not an inline <script> living
   inside snippets/tc-delivery-estimate.liquid or snippets/cart-line-items.liquid)
   for the same reason as assets/coded-price.js: the cart drawer's AJAX-fetched
   line items (sections/api-cart-items.liquid, rendered into [data-line-items]
   via innerHTML) never execute embedded <script> tags, so per-snippet inline
   scripts would never run on a drawer refresh. A single global script that
   re-scans the DOM covers the PDP block, the /cart page, and drawer refreshes
   alike.

   Namespace: window.tcEdd. No dependencies. ~3KB unminified.

   CONFIG below is a hardcoded fallback that mirrors the Liquid config at the
   top of snippets/tc-delivery-estimate.liquid, which is the documented,
   editable source of truth for the business numbers. On any page that renders
   the PDP block, that snippet also prints a #tc-edd-config JSON island with
   the SAME numbers, and applyDomConfig() (below) overrides these defaults
   with it at startup - so a config change to the Liquid file takes effect
   everywhere without touching this file. The hardcoded copy only matters on
   pages that render the cart (drawer / /cart) without the PDP block present,
   where no such island exists. Keep the two numerically in sync regardless -
   search "KEEP IN SYNC" in the Liquid file when you change a number here.

   Two PDP layouts share this script. [data-tc-edd] without a layout attribute
   is the stand-alone delivery_estimate block (MM/DD/YYYY, "16 h 36 m").
   [data-tc-edd-layout="row"] is the delivery row of the buy_info list
   (issue #695): "Delivery before Wed, Nov 4", then "Order within 13 hours"
   before today's cutoff or "Order by 11 AM PT on Mon, Nov 2" after it. The
   cart surfaces ([data-tc-edd-cart-min] / [data-tc-edd-cart-max]) keep
   MM/DD/YYYY either way. */
(function () {
  'use strict';
  if (window.tcEdd) return;

  var CUTOFF_HOUR = 11;
  var CUTOFF_MINUTE = 0;
  var CUTOFF_TZ = 'America/Los_Angeles';
  var WORKING_DAYS = { Mon: true, Tue: true, Wed: true, Thu: true, Fri: true, Sat: false, Sun: false };

  // Blackout dates (YYYY-MM-DD, PT calendar date). Ships EMPTY per Kevin
  // 2026-09-10 (the old Omega config had stale 2024 dates plus 7/4/2025; no
  // holiday blackouts today). Add future dates here as plain strings.
  var BLACKOUT = [];

  // Zone table: CALENDAR days added to the ship day. "fallback" = non-US /
  // unrecognized timezone and is exact parity with the pre-native Omega
  // national window (min 2 / max 8, incl. the +1 padding day).
  // Max values include +1 padding day (Kevin 2026-09-10: calendar days
  // from warehouse data, plus one safety day on the max date only).
  var ZONES = {
    pacific: { min: 2, max: 7 },
    mountain: { min: 3, max: 8 },
    central: { min: 4, max: 9 },
    eastern: { min: 5, max: 10 },
    fallback: { min: 2, max: 8 }
  };

  // Intl.DateTimeFormat().resolvedOptions().timeZone -> zone key. Only US
  // zones are mapped; anything else (or an unrecognized value) falls back.
  var TZ_TO_ZONE = {
    'America/Los_Angeles': 'pacific',

    'America/Denver': 'mountain',
    'America/Phoenix': 'mountain',
    'America/Boise': 'mountain',

    'America/Chicago': 'central',
    'America/Indiana/Knox': 'central',
    'America/Indiana/Tell_City': 'central',
    'America/Menominee': 'central',
    'America/North_Dakota/Beulah': 'central',
    'America/North_Dakota/Center': 'central',
    'America/North_Dakota/New_Salem': 'central',

    'America/New_York': 'eastern',
    'America/Detroit': 'eastern',
    'America/Indiana/Indianapolis': 'eastern',
    'America/Indiana/Marengo': 'eastern',
    'America/Indiana/Petersburg': 'eastern',
    'America/Indiana/Vevay': 'eastern',
    'America/Indiana/Vincennes': 'eastern',
    'America/Indiana/Winamac': 'eastern',
    'America/Kentucky/Louisville': 'eastern',
    'America/Kentucky/Monticello': 'eastern'
  };

  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Row layout names (issue #695). Fixed English arrays so the PDP row never
  // depends on the visitor's locale settings.
  var DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Liquid (snippets/tc-delivery-estimate.liquid) is the documented source of
  // truth: whenever the PDP block is on the page it renders the SAME numbers
  // above into #tc-edd-config, and this overrides the hardcoded copy with it.
  // Pages that render only the cart (drawer / /cart, no PDP block) don't have
  // that element, so they keep running on the hardcoded defaults above -
  // identical numbers, just not Liquid-sourced on those pages.
  function applyDomConfig() {
    var el = document.getElementById('tc-edd-config');
    if (!el) return;
    var cfg;
    try {
      cfg = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    if (typeof cfg.cutoffHour === 'number') CUTOFF_HOUR = cfg.cutoffHour;
    if (typeof cfg.cutoffMinute === 'number') CUTOFF_MINUTE = cfg.cutoffMinute;
    if (Array.isArray(cfg.workingDays)) {
      var wd = {};
      DOW.forEach(function (d) {
        wd[d] = false;
      });
      cfg.workingDays.forEach(function (d) {
        wd[d] = true;
      });
      WORKING_DAYS = wd;
    }
    if (Array.isArray(cfg.blackout)) BLACKOUT = cfg.blackout;
    if (cfg.zones) {
      Object.keys(cfg.zones).forEach(function (key) {
        var z = cfg.zones[key];
        if (z && typeof z.min === 'number' && typeof z.max === 'number') {
          ZONES[key] = { min: z.min, max: z.max };
        }
      });
    }
  }

  // Reads the current wall-clock date/time IN Pacific time via Intl, never a
  // fixed UTC offset, so this stays correct across the PST/PDT transitions.
  function ptParts(date) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: CUTOFF_TZ,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short'
    });
    var parts = {};
    fmt.formatToParts(date).forEach(function (p) {
      parts[p.type] = p.value;
    });
    var hour = parseInt(parts.hour, 10);
    return {
      year: parseInt(parts.year, 10),
      month: parseInt(parts.month, 10),
      day: parseInt(parts.day, 10),
      hour: hour === 24 ? 0 : hour,
      minute: parseInt(parts.minute, 10),
      weekday: parts.weekday
    };
  }

  // Pure calendar-date arithmetic, independent of any real timezone: the Y/M/D
  // is pinned at UTC noon so adding days never risks crossing a DST boundary.
  function toUTCNoon(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  }
  function addDays(utcNoonDate, n) {
    var d = new Date(utcNoonDate.getTime());
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  }
  function weekdayShort(utcNoonDate) {
    return DOW[utcNoonDate.getUTCDay()];
  }
  function isoDate(utcNoonDate) {
    return utcNoonDate.toISOString().slice(0, 10);
  }
  function isBlackout(utcNoonDate) {
    return BLACKOUT.indexOf(isoDate(utcNoonDate)) !== -1;
  }
  function isWorkingDay(utcNoonDate) {
    return !!WORKING_DAYS[weekdayShort(utcNoonDate)] && !isBlackout(utcNoonDate);
  }
  function nextWorkingDay(utcNoonDate) {
    var d = addDays(utcNoonDate, 1);
    var guard = 0;
    while (!isWorkingDay(d) && guard < 30) {
      d = addDays(d, 1);
      guard++;
    }
    return d;
  }
  function rollSunday(utcNoonDate) {
    return weekdayShort(utcNoonDate) === 'Sun' ? addDays(utcNoonDate, 1) : utcNoonDate;
  }
  function formatDisplay(utcNoonDate) {
    // MM/DD/YYYY to match the Omega widget's live date_format (parity capture 2026-09-10)
    return pad2(utcNoonDate.getUTCMonth() + 1) + '/' + pad2(utcNoonDate.getUTCDate()) + '/' + utcNoonDate.getUTCFullYear();
  }

  function detectZone() {
    var tz;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      tz = null;
    }
    return TZ_TO_ZONE[tz] || 'fallback';
  }

  // Converts a Pacific wall-clock Y/M/D H:M to the real UTC instant it
  // represents. One correction pass is enough: the PT UTC offset only changes
  // by a whole hour at DST transitions, and this cutoff (11:00) never sits
  // inside the "skipped"/"repeated" hour those transitions create.
  function ptWallClockToUtcMs(y, m, d, hour, minute) {
    var guessUtc = Date.UTC(y, m - 1, d, hour, minute, 0);
    var guessed = ptParts(new Date(guessUtc));
    var guessedAsUtc = Date.UTC(guessed.year, guessed.month - 1, guessed.day, guessed.hour, guessed.minute, 0);
    return guessUtc + (guessUtc - guessedAsUtc);
  }

  function compute(now) {
    now = now || new Date();
    var pt = ptParts(now);
    var todayNoon = toUTCNoon(pt.year, pt.month, pt.day);
    var beforeCutoff = pt.hour < CUTOFF_HOUR || (pt.hour === CUTOFF_HOUR && pt.minute < CUTOFF_MINUTE);

    var orderDay = isWorkingDay(todayNoon) && beforeCutoff ? todayNoon : nextWorkingDay(todayNoon);

    var zoneKey = detectZone();
    var zone = ZONES[zoneKey] || ZONES.fallback;

    var minDate = rollSunday(addDays(orderDay, zone.min));
    var maxDate = rollSunday(addDays(orderDay, zone.max));

    var countdownTargetMs = ptWallClockToUtcMs(
      orderDay.getUTCFullYear(),
      orderDay.getUTCMonth() + 1,
      orderDay.getUTCDate(),
      CUTOFF_HOUR,
      CUTOFF_MINUTE
    );

    var orderDayIsToday = orderDay.getTime() === todayNoon.getTime();

    return {
      zone: zoneKey,
      minDisplay: formatDisplay(minDate),
      maxDisplay: formatDisplay(maxDate),
      countdownTargetMs: countdownTargetMs,
      orderDayIsToday: orderDayIsToday,
      orderDayDisplay: formatDisplay(orderDay),
      // Row layout (#695) formats these itself; the MM/DD/YYYY fields above
      // stay exactly as they were for the cart drawer and /cart.
      maxDate: maxDate,
      orderDay: orderDay
    };
  }

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }
  function formatCountdown(ms) {
    // "16 h 36 m" units-with-spaces, no seconds, matching the live Omega
    // widget's rendering (parity capture 2026-09-10). Minutes are floored
    // to match Omega's displayed minute (conservative for a deadline).
    if (ms <= 0) return '0 h 00 m';
    var totalMinutes = Math.floor(ms / 60000);
    var h = Math.floor(totalMinutes / 60);
    var m = totalMinutes % 60;
    return h + ' h ' + pad2(m) + ' m';
  }
  // Derives the "H:MM AM/PM" cutoff label from CUTOFF_HOUR/CUTOFF_MINUTE
  // instead of a hardcoded string, so the accessible sentence stays correct
  // if applyDomConfig() overrides the cutoff (Codex review finding, verified
  // real: this used to say "11:00 AM" literally regardless of config).
  function formatCutoffLabel() {
    var period = CUTOFF_HOUR < 12 ? 'AM' : 'PM';
    var hour12 = CUTOFF_HOUR % 12;
    if (hour12 === 0) hour12 = 12;
    return hour12 + ':' + pad2(CUTOFF_MINUTE) + ' ' + period;
  }
  function staticSentence(c) {
    var when = c.orderDayIsToday ? 'today' : 'on ' + c.orderDayDisplay;
    return 'Order by ' + formatCutoffLabel() + ' Pacific ' + when + ' for delivery by ' + c.maxDisplay;
  }

  // ---- Row layout (issue #695), used by the buy_info list on the PDP ----
  // "Wed, Nov 4" (visible) and "Wednesday, November 4" (screen readers).
  function formatRowShort(utcNoonDate) {
    return DOW[utcNoonDate.getUTCDay()] + ', ' + MONTH_SHORT[utcNoonDate.getUTCMonth()] + ' ' + utcNoonDate.getUTCDate();
  }
  function formatRowLong(utcNoonDate) {
    return DOW_LONG[utcNoonDate.getUTCDay()] + ', ' + MONTH_LONG[utcNoonDate.getUTCMonth()] + ' ' + utcNoonDate.getUTCDate();
  }
  // "11 AM" (or "11:30 AM"), derived from the config like formatCutoffLabel().
  function formatCutoffShort() {
    var period = CUTOFF_HOUR < 12 ? 'AM' : 'PM';
    var hour12 = CUTOFF_HOUR % 12;
    if (hour12 === 0) hour12 = 12;
    return hour12 + (CUTOFF_MINUTE ? ':' + pad2(CUTOFF_MINUTE) : '') + ' ' + period;
  }
  // Second line. Coarse on purpose: whole hours, then whole minutes in the
  // last hour, always floored (a deadline should never read later than it
  // is) and never below 1.
  function rowCountdownText(c, remainingMs) {
    if (c.orderDayIsToday) {
      var hours = Math.floor(remainingMs / 3600000);
      if (hours >= 1) return 'Order within ' + hours + (hours === 1 ? ' hour' : ' hours');
      var minutes = Math.max(1, Math.floor(remainingMs / 60000));
      return 'Order within ' + minutes + (minutes === 1 ? ' minute' : ' minutes');
    }
    return 'Order by ' + formatCutoffShort() + ' PT on ' + formatRowShort(c.orderDay);
  }
  function rowStaticSentence(c) {
    var when = c.orderDayIsToday ? 'today' : 'on ' + formatRowLong(c.orderDay);
    return 'Order by ' + formatCutoffLabel() + ' Pacific ' + when + ' for delivery before ' + formatRowLong(c.maxDate) + '.';
  }
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }
  function countdownText(root, c, remainingMs) {
    return root.getAttribute('data-tc-edd-layout') === 'row' ? rowCountdownText(c, remainingMs) : formatCountdown(remainingMs);
  }

  var state = null;
  var tickTimer = null;

  function applyPdp(c) {
    var roots = document.querySelectorAll('[data-tc-edd]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var row = root.getAttribute('data-tc-edd-layout') === 'row';
      setText(root.querySelector('[data-tc-edd-date]'), row ? formatRowShort(c.maxDate) : c.maxDisplay);
      setText(root.querySelector('[data-tc-edd-static]'), row ? rowStaticSentence(c) : staticSentence(c));
      setText(root.querySelector('[data-tc-edd-countdown]'), countdownText(root, c, c.countdownTargetMs - Date.now()));
    }
  }

  // Guards every write with a same-value check: applyCart runs from inside
  // observeCart()'s MutationObserver callback, and setting .textContent
  // (even to an unchanged value) itself fires a childList mutation on that
  // node. Without the guard, that would re-trigger the observer forever.
  function applyCart(c) {
    var mins = document.querySelectorAll('[data-tc-edd-cart-min]');
    var maxes = document.querySelectorAll('[data-tc-edd-cart-max]');
    var i;
    for (i = 0; i < mins.length; i++) {
      if (mins[i].textContent !== c.minDisplay) mins[i].textContent = c.minDisplay;
    }
    for (i = 0; i < maxes.length; i++) {
      if (maxes[i].textContent !== c.maxDisplay) maxes[i].textContent = c.maxDisplay;
    }
  }

  function refresh() {
    state = compute();
    applyPdp(state);
    applyCart(state);
  }

  function tick() {
    if (!state) return;
    var remaining = state.countdownTargetMs - Date.now();
    if (remaining <= 0) {
      refresh();
      return;
    }
    var roots = document.querySelectorAll('[data-tc-edd]');
    for (var i = 0; i < roots.length; i++) {
      setText(roots[i].querySelector('[data-tc-edd-countdown]'), countdownText(roots[i], state, remaining));
    }
  }

  // The cart drawer's AJAX refresh replaces [data-line-items] via innerHTML
  // (see sections/api-cart-items.liquid -> snippets/cart-line-items.liquid),
  // which never re-fires this already-loaded script, so newly injected cart
  // rows would otherwise be stuck on their server-rendered fallback window.
  // A MutationObserver sidesteps needing to know theme.js's internal (and
  // minified) cart-update event name.
  var cartObserverTarget = null;
  function observeCart() {
    var target = document.querySelector('[data-line-items]');
    if (!target || target === cartObserverTarget) return;
    cartObserverTarget = target;
    var mo = new MutationObserver(function () {
      applyCart(state || compute());
    });
    mo.observe(target, { childList: true, subtree: true });
  }

  function init() {
    applyDomConfig();
    refresh();
    if (tickTimer) clearInterval(tickTimer);
    tickTimer = setInterval(tick, 1000);
    observeCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme-editor section re-renders swap markup without executing inline
  // scripts (same landmine documented on the PDP gallery sync in
  // sections/product.liquid); this script is a persistent global asset, so it
  // only needs to re-scan and reattach, not reload.
  document.addEventListener('shopify:section:load', function () {
    applyDomConfig();
    refresh();
    observeCart();
  });

  window.tcEdd = { compute: compute, refresh: refresh };
})();
